import { Heart, ArrowRight, Target, Users, Award } from "lucide-react";
import { FAI_MONTE_FAETA_LINK } from "../constants";

const GOAL = 3000;

export const MonteFaeta = () => {
  return (
    <section
      id="monte-faeta"
      data-testid="monte-faeta-section"
      className="relative py-24 sm:py-32 bg-gradient-to-br from-[#2E4F3B] via-[#1f3828] to-[#0f1a14] text-white overflow-hidden"
    >
      {/* Glow */}
      <div className="pointer-events-none absolute -top-40 -left-32 w-[600px] h-[600px] rounded-full bg-[#E8C9A0]/[0.10] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#7BA577]/[0.14] blur-3xl" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "repeating-radial-gradient(circle at 50% 50%, transparent 0, transparent 40px, #E8C9A0 40px, #E8C9A0 41px)",
        }}
      />

      <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center">
          {/* FAI badge + logo placeholder */}
          <div className="inline-flex items-center gap-3 rounded-full bg-white px-4 py-2 mb-7 shadow-lg">
            {/* Logo FAI — sostituire src con il logo ufficiale quando disponibile */}
            <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-[#E8492A]/10 border border-[#E8492A]/30 text-[#E8492A] font-black text-sm tracking-tight">
              FAI
            </span>
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.18em] text-[#2E4F3B]">
              I Luoghi del Cuore · Censimento Nazionale
            </span>
          </div>

          {/* Eyebrow azione */}
          <div className="inline-flex items-center gap-2 rounded-full bg-[#C15C3D]/25 border border-[#C15C3D]/50 px-4 py-1.5 mb-6">
            <Award className="w-3.5 h-3.5 text-[#E8C9A0]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-[#E8C9A0]">
              Azione in corso · Aiuta con la tua firma
            </span>
          </div>

          <h2
            data-testid="monte-faeta-title"
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.9] tracking-tight"
          >
            Salviamo
            <br />
            <span className="italic font-light text-[#E8C9A0]">
              Monte Faeta.
            </span>
          </h2>

          <p className="mt-7 text-lg sm:text-xl text-white/90 leading-relaxed max-w-2xl mx-auto">
            Monte Faeta è candidato al{" "}
            <strong className="text-white">censimento nazionale "I Luoghi del Cuore"</strong>{" "}
            promosso dal <strong className="text-white">FAI — Fondo per
            l'Ambiente Italiano</strong>. La tua firma è gratuita e ci aiuta a
            sostenere la tutela e la valorizzazione del nostro territorio.
          </p>

          {/* Stats */}
          <div className="mt-10 grid grid-cols-2 gap-5 max-w-md mx-auto">
            <div className="rounded-2xl bg-white/10 backdrop-blur border border-white/15 p-5">
              <Target className="w-5 h-5 text-[#E8C9A0] mx-auto mb-2" />
              <div className="font-display text-3xl sm:text-4xl font-black tabular-nums">
                {GOAL.toLocaleString("it-IT")}
              </div>
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-white/65 mt-1">
                Firme obiettivo
              </div>
            </div>
            <div className="rounded-2xl bg-white/10 backdrop-blur border border-white/15 p-5">
              <Users className="w-5 h-5 text-[#E8C9A0] mx-auto mb-2" />
              <div className="font-display text-3xl sm:text-4xl font-black tabular-nums">
                100%
              </div>
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-white/65 mt-1">
                Gratuito · 30 sec
              </div>
            </div>
          </div>

          {/* CTA principale rafforzata */}
          <a
            href={FAI_MONTE_FAETA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="monte-faeta-cta"
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#E8C9A0] hover:bg-[#d9b380] text-[#1f3828] font-black text-base sm:text-xl px-9 py-5 sm:px-11 sm:py-6 transition-all hover:-translate-y-1 shadow-[0_15px_40px_rgba(232,201,160,0.35)] hover:shadow-[0_25px_60px_rgba(232,201,160,0.55)] group"
          >
            <Heart className="w-5 h-5 sm:w-6 sm:h-6 fill-current" />
            Firma per sostenere il Monte Faeta
            <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 transition-transform group-hover:translate-x-1" />
          </a>

          <p className="mt-5 text-xs sm:text-sm text-white/65 max-w-md mx-auto">
            Cliccando vieni reindirizzato al sito ufficiale FAI per firmare in
            sicurezza. Bastano 30 secondi.
          </p>

          {/* Trust signals */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs sm:text-sm text-white/70">
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E8C9A0]" />
              Censimento ufficiale FAI
            </span>
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E8C9A0]" />
              Apertura nuova scheda
            </span>
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E8C9A0]" />
              Nessun dato richiesto dall'associazione
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
